import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowRight, Lock } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success(`Bienvenido, ${u.name}`);
      nav(u.role === "super_admin" ? "/super-admin" : "/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error al iniciar sesión");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2" data-testid="login-page">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-8 sm:px-16 py-12 bg-white">
        <button
          onClick={() => nav("/")}
          className="text-sm tracking-label text-slate-500 hover:text-slate-900 self-start mb-12"
          data-testid="back-home-link"
        >
          ← Volver
        </button>
        <div className="max-w-sm w-full">
          <div className="flex items-center gap-3 mb-10">
            <div className="w-10 h-10 bg-slate-900 grid place-items-center rounded">
              <span className="text-white font-display font-black text-lg">W</span>
            </div>
            <div>
              <div className="font-display font-bold text-slate-900 text-lg leading-none">
                Noctua 360
              </div>
              <div className="tracking-label text-slate-500">Acceso seguro</div>
            </div>
          </div>
          <h1 className="font-display font-black text-3xl sm:text-4xl text-slate-900">
            Inicia sesión
          </h1>
          <p className="mt-2 text-sm text-slate-600">
            Para administradores y coordinadores. Los voluntarios se registran
            mediante enlaces de reclutamiento.
          </p>

          <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="login-form">
            <div>
              <Label htmlFor="email" className="text-xs uppercase tracking-[0.2em] text-slate-500">
                Correo electrónico
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-2 h-11"
                data-testid="login-email-input"
                autoComplete="email"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-xs uppercase tracking-[0.2em] text-slate-500">
                Contraseña
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="mt-2 h-11"
                data-testid="login-password-input"
                autoComplete="current-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full h-11"
              data-testid="login-submit-button"
            >
              {loading ? "Validando…" : "Entrar"}
              {!loading && <ArrowRight className="ml-2 h-4 w-4" />}
            </Button>
          </form>

          <div className="mt-6 flex items-center gap-2 text-xs text-slate-500">
            <Lock className="w-3.5 h-3.5" />
            <span>Conexión cifrada · Cumple LFPDPPP</span>
          </div>
        </div>
      </div>

      {/* Right: branding panel */}
      <div className="hidden lg:flex relative overflow-hidden bg-slate-900 text-white">
        <div className="absolute inset-0 line-pattern opacity-30" />
        <div className="relative z-10 flex flex-col justify-between p-16 w-full">
          <div className="brand-stripe w-24" />
          <div>
            <h2 className="font-display font-black text-4xl xl:text-5xl leading-tight">
              Operaciones territoriales con
              <br />
              <span className="text-blue-400">precisión geoelectoral.</span>
            </h2>
            <p className="mt-6 text-slate-300 max-w-md leading-relaxed">
              Multi-tenant. Multi-rol. Auditado. Tu estructura de campaña con
              cobertura por sección, manzana y colonia, alimentada por el
              catálogo oficial del INE.
            </p>
          </div>
          <div className="grid grid-cols-2 gap-8 max-w-md">
            <div>
              <div className="tracking-label text-blue-400">Subcuentas</div>
              <div className="font-display font-black text-2xl">Independientes</div>
            </div>
            <div>
              <div className="tracking-label text-blue-400">Aprobaciones</div>
              <div className="font-display font-black text-2xl">Trazables</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
